import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(undefined);

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    // Check server-injected org default (available synchronously before API fetch)
    if (window.SITE_CONFIG?.defaults?.default_dark_mode) {
      return true;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleTheme = () => {
    setDarkMode(prev => !prev);
  };

  // Called from SiteConfigContext/AdminStyleSelector when org default changes (via config)
  useEffect(() => {
    const handleOrgDefault = (e) => {
      setDarkMode(!!e.detail);
    };
    window.addEventListener('orgDarkModeDefault', handleOrgDefault);
    return () => window.removeEventListener('orgDarkModeDefault', handleOrgDefault);
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme: darkMode ? 'dark' : 'light',
      darkMode,
      toggleTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
